import { useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { Building2, Bus, Users } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/nucleo/auth/useAuth';
import { ehSoDre } from '@/nucleo/navegacao/apresentacao';
import { QuadroDeEstado } from '@/modulos/operacao/analises/componentes';
import { useColecao } from '@/modulos/operacao/analises/useColecao';
import { DataTable } from '@/ui/DataTable';
import { MobileCardList } from '@/ui/MobileCardList';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid } from '@/ui/StatsGrid';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { type TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';

import type { CoberturaDaDre } from './contrato';
import { carregarCoberturaPorDre } from './dados';

/**
 * Cobertura por DRE (`/sme/cobertura`) — visão consolidada da rede de
 * transporte por diretoria: escolas, OS em execução e alunos atendidos.
 *
 * Só perfis de SME (e admin) enxergam a rede inteira. Quem é só DRE volta ao
 * início da área com aviso, como no protótipo — o item também não aparece no
 * menu dele (`PAPEIS_SOMENTE_SME` em `mapa.ts`), mas a URL digitada à mão
 * precisa da mesma resposta (regra 7 de interface).
 */

const EMPTY_TITULO = 'Sem DREs vinculadas';
const EMPTY_DESCRICAO = 'Solicite ao administrador o vínculo da sua DRE.';

function formatar(valor: number): string {
  return valor.toLocaleString('pt-BR');
}

function coberturaVazia(dado: readonly CoberturaDaDre[]): boolean {
  return dado.length === 0;
}

const COLUNAS: TableViewColumn<CoberturaDaDre & { id: string }>[] = [
  {
    id: 'nome',
    header: 'DRE',
    accessorKey: 'dreNome',
    sortKey: (r) => r.dreNome.toLowerCase(),
    isCritical: true,
    canHide: false,
  },
  {
    id: 'escolas',
    header: 'Escolas',
    cell: (r) => formatar(r.escolas),
    sortKey: 'escolas',
    cellClassName: 'text-right tabular-nums',
    headerClassName: 'text-right',
  },
  {
    id: 'os',
    header: 'OS ativas',
    cell: (r) => formatar(r.osAtivas),
    sortKey: 'osAtivas',
    cellClassName: 'text-right tabular-nums',
    headerClassName: 'text-right',
  },
  {
    id: 'alunos',
    header: 'Alunos atendidos',
    cell: (r) => formatar(r.alunosAtendidos),
    sortKey: 'alunosAtendidos',
    cellClassName: 'text-right tabular-nums',
    headerClassName: 'text-right',
  },
];

export function CoberturaPorDrePage() {
  const { usuario } = useAuth();
  const soDre = ehSoDre(usuario?.papeis);

  useEffect(() => {
    if (soDre) {
      toast('Visão consolidada', {
        description: 'A cobertura por DRE é exibida apenas para perfis SME.',
      });
    }
  }, [soDre]);

  if (soDre) {
    return <Navigate to="/sme" replace />;
  }

  return <Conteudo />;
}

function Conteudo() {
  const { estado, recarregar } = useColecao(carregarCoberturaPorDre, coberturaVazia);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Cobertura por DRE"
        description="Visão consolidada da rede de transporte sob responsabilidade da SME"
      />

      <QuadroDeEstado
        estado={estado}
        recarregar={recarregar}
        tituloVazio={EMPTY_TITULO}
        descricaoVazio={EMPTY_DESCRICAO}
      >
        {(dado) => <Painel linhas={dado} />}
      </QuadroDeEstado>
    </div>
  );
}

function Painel({ linhas }: { linhas: readonly CoberturaDaDre[] }) {
  const totais = useMemo(
    () =>
      linhas.reduce(
        (acc, r) => ({
          escolas: acc.escolas + r.escolas,
          os: acc.os + r.osAtivas,
          alunos: acc.alunos + r.alunosAtendidos,
        }),
        { escolas: 0, os: 0, alunos: 0 },
      ),
    [linhas],
  );

  const dadosDaTabela = useMemo(() => linhas.map((r) => ({ ...r, id: r.dreId })), [linhas]);

  const tableView = useTableViewPreferences({
    contextKey: 'sme:cobertura',
    columns: COLUNAS,
  });

  return (
    <>
      <StatsGrid
        columns="grid-cols-1 sm:grid-cols-3"
        stats={[
          { title: 'Escolas', value: formatar(totais.escolas), icon: Building2 },
          { title: 'OS em execução', value: formatar(totais.os), icon: Bus },
          { title: 'Alunos atendidos', value: formatar(totais.alunos), icon: Users },
        ]}
      />

      <Card>
        <CardHeader>
          <CardTitle>Por DRE</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Abaixo de 640 px a tabela vira cartões, sem rolagem horizontal. */}
          <div className="sm:hidden">
            <MobileCardList
              emptyTitle={EMPTY_TITULO}
              emptyDescription={EMPTY_DESCRICAO}
              items={linhas.map((r) => ({
                id: r.dreId,
                title: r.dreNome,
                fields: [
                  { label: 'Escolas', value: formatar(r.escolas) },
                  { label: 'OS ativas', value: formatar(r.osAtivas) },
                  { label: 'Alunos atendidos', value: formatar(r.alunosAtendidos), full: true },
                ],
              }))}
            />
          </div>

          <div className="hidden sm:block">
            <DataTable
              columns={tableView.orderedColumns}
              personalizacao={tableView}
              data={dadosDaTabela}
              emptyTitle={EMPTY_TITULO}
              emptyDescription={EMPTY_DESCRICAO}
              ariaLabel="Cobertura por DRE"
            />
          </div>
        </CardContent>
      </Card>
    </>
  );
}

export default CoberturaPorDrePage;
